/**
 * Zoom Overview Types (Frontend)
 *
 * Mirrors the backend ZoomOverviewData shape.
 */

export type Scope = 'admin' | 'user';

export interface NotAvailable {
  _status: 'not_available';
  message: string;
}

export interface AccountData {
  id: string;
  name: string;
  email: string;
  type: number;
  typeName: string;
  picUrl?: string;
  timezone?: string;
  createdAt?: string;
  vanityUrl?: string;
  managedDomains?: string[];
  scopeWarning?: string;
  ownerName?: string;
  ownerPicUrl?: string;
}

export interface PlansData {
  _scope: Scope;
  planBase?: { type: string; hosts: number };
  planLargeMeeting?: Array<{ type: string; hosts: number }>;
  planWebinar?: Array<{ type: string; hosts: number }>;
  planZoomRooms?: { type: string; hosts: number };
}

export interface UserSummary {
  email: string;
  name: string;
  type: number;
  typeName: string;
  role?: string;
  status?: string;
  lastLogin?: string;
  department?: string;
}

export interface UsersData {
  _scope: Scope;
  totalRecords: number;
  users: UserSummary[];
  byType: Record<string, number>;
  byStatus: Record<string, number>;
}

export interface RoleSummary {
  id: string;
  name: string;
  description?: string;
  totalMembers: number;
}

export interface RolesData {
  totalRoles: number;
  roles: RoleSummary[];
}

export interface GroupSummary {
  id: string;
  name: string;
  totalMembers: number;
}

export interface GroupsData {
  totalGroups: number;
  groups: GroupSummary[];
}

export interface SettingsData {
  _scope: Scope;
  security: {
    waitingRoom?: boolean;
    requirePassword?: boolean;
    embedPasswordInLink?: boolean;
    e2eEncryption?: boolean;
    signedLogin?: boolean;
    approvedDomains?: string[];
  };
  meeting: {
    hostVideo?: boolean;
    participantVideo?: boolean;
    joinBeforeHost?: boolean;
    muteUponEntry?: boolean;
    autoRecording?: string;
    screenSharing?: boolean;
    whoCanShare?: string;
    breakoutRoom?: boolean;
  };
  recording: {
    localRecording?: boolean;
    cloudRecording?: boolean;
    autoDelete?: boolean;
    autoDeleteDays?: number;
    recordingDisclaimer?: boolean;
  };
}

export interface ParticipantDetail {
  email: string;
  name: string;
}

export interface DailyUsage {
  date: string;
  meetings: number;
  participants: number;
  meetingMinutes: number;
  newUsers: number;
  uniqueParticipants?: number;
  participantDetails?: ParticipantDetail[];
}

export interface MonthlyUsage {
  month: string; // "2026-03"
  meetings: number;
  participants: number;
  meetingMinutes: number;
  newUsers: number;
  uniqueParticipants?: number;
  participantDetails?: ParticipantDetail[];
}

export interface TopMeeting {
  topic: string;
  occurrences: number;
  avgParticipants: number;
  avgDuration: number;
  totalDuration: number;
  lastOccurred: string;
  host: string;
}

export interface UsageData {
  _scope: Scope;
  year: number;
  month: number;
  totalMeetings: number;
  totalParticipants: number;
  totalMeetingMinutes: number;
  dailyBreakdown: DailyUsage[];
  monthlyBreakdown: MonthlyUsage[];
  topMeetings?: TopMeeting[];
}

export interface RecordingsData {
  _scope: Scope;
  totalRecords: number;
  totalSize: number;
  cloudRecordings: Array<{
    topic: string;
    startTime: string;
    duration: number;
    totalSize: number;
    recordingCount: number;
  }>;
}

export interface PhoneData {
  _status: 'provisioned' | 'not_provisioned';
  companyName?: string;
  autoCallRecording?: boolean;
  emergencyServiceEnabled?: boolean;
  voicemailEnabled?: boolean;
  totalUsers?: number;
}

export interface RoomsData {
  _status: 'provisioned' | 'not_provisioned';
  totalRooms?: number;
  rooms?: Array<{
    id: string;
    name: string;
    status: string;
  }>;
}

export interface MarketplaceData {
  totalApps: number;
  apps: Array<{
    name: string;
    status?: string;
  }>;
}

export interface ArchiveData {
  _status: 'provisioned' | 'not_provisioned';
  totalFiles?: number;
  totalSize?: number;
}

// --- Meeting Quality ---

export interface MeetingQualityData {
  _scope: Scope;
  totalMeetings: number;
  qualityDistribution: { good: number; fair: number; poor: number; bad: number };
  avgQualityScore?: number;
  recentMeetings: Array<{
    uuid: string;
    topic: string;
    startTime: string;
    duration: number;
    participants: number;
    quality: string;
  }>;
}

// --- Participant QoS ---

export interface QosMetrics {
  bitrate?: string;
  latency?: string;
  jitter?: string;
  packetLoss?: string;
  resolution?: string;
  frameRate?: string;
}

export interface ParticipantQosData {
  meetingUuid: string;
  participants: Array<{
    name: string;
    email?: string;
    device?: string;
    audio: QosMetrics;
    video: QosMetrics;
    screenShare?: QosMetrics;
  }>;
}

// --- Client Satisfaction ---

export interface SatisfactionData {
  _scope: Scope;
  totalResponses: number;
  goodCount: number;
  notGoodCount: number;
  goodPercent: number;
  dateRange: { from: string; to: string };
}

// --- Phone Call Logs ---

export interface PhoneCallLogsData {
  _scope: Scope;
  totalCalls: number;
  avgDurationSeconds: number;
  byDirection: { inbound: number; outbound: number };
  recentCalls: Array<{
    callerName?: string;
    callerNumber?: string;
    calleeName?: string;
    calleeNumber?: string;
    direction: string;
    duration: number;
    startTime: string;
    callType?: string;
  }>;
}

// --- Telephone Report ---

export interface TelephoneData {
  _scope: Scope;
  totalMinutes: number;
  totalCalls: number;
  byType: Record<string, number>;
}

// --- Live Meetings ---

export interface LiveMeetingsData {
  _scope: Scope;
  totalLive: number;
  meetings: Array<{
    uuid: string;
    topic: string;
    host: string;
    startTime: string;
    participants: number;
    hasIssues: boolean;
  }>;
}

// --- Room Metrics ---

export interface RoomMetricsData {
  _scope: Scope;
  totalRooms: number;
  online: number;
  offline: number;
  issues: Array<{ roomName: string; issue: string }>;
}

// --- Operation Logs ---

export interface OperationLogsData {
  _scope: Scope;
  totalLogs: number;
  logs: Array<{
    operator: string;
    action: string;
    categoryType: string;
    time: string;
  }>;
}

export interface ZoomOverviewData {
  fetchedAt: string;
  scope: Scope;
  account: AccountData;
  settings: SettingsData;
  plans: PlansData | NotAvailable;
  users: UsersData;
  roles: RolesData | NotAvailable;
  groups: GroupsData | NotAvailable;
  usage: UsageData | NotAvailable;
  recordings: RecordingsData;
  phone: PhoneData;
  rooms: RoomsData;
  marketplace: MarketplaceData | NotAvailable;
  archive: ArchiveData;
  meetingQuality?: MeetingQualityData | NotAvailable;
  satisfaction?: SatisfactionData | NotAvailable;
  phoneCallLogs?: PhoneCallLogsData | NotAvailable;
  telephone?: TelephoneData | NotAvailable;
  liveMeetings?: LiveMeetingsData | NotAvailable;
  roomMetrics?: RoomMetricsData | NotAvailable;
  operationLogs?: OperationLogsData | NotAvailable;
  aiSummary?: {
    scores: {
      security: number;
      adoption: number;
      usage: number;
      configuration: number;
      overall: number;
    };
    keyPoints: string[];
    suggestions: string[];
    overallEvaluation: string;
  };
}

// Type guard
export function isNotAvailable(v: unknown): v is NotAvailable {
  return typeof v === 'object' && v !== null && '_status' in v && (v as any)._status === 'not_available';
}
